import Layout from '@/components/layout/Layout';
import { Link } from 'react-router-dom';
import HeroSection from '@/components/home/HeroSection';
import PricingSection from '@/components/home/PricingSection';
import WhyChooseUs from '@/components/home/WhyChooseUs';
import BlogPreview from '@/components/home/BlogPreview';
import CTASection from '@/components/home/CTASection';
import { useState, useEffect } from 'react';
import { useAuth } from "@/hooks/useAuth";
import { Progress } from '@/components/ui/progress';
import { PlayCircle, ChevronRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

const CourseProgress = () => {
  const { user } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const fetchProgress = async () => {
      try {
        const data = await safeFetch<any[]>(
          supabase
            .from('enrollments')
            .select('progress, courses(title, id)')
            .eq('user_id', user.id)
            .limit(2),
          [],
          'Scanning Recent Nodes'
        );

        const mapped = data.map(en => ({
          id: en.courses?.id,
          title: en.courses?.title,
          progress: en.progress || 0
        })).filter(c => c.id);

        setEnrolledCourses(mapped);
      } catch (error) {
        console.error('Failed to load progress from Supabase');
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [user]);

  if (!user || loading || enrolledCourses.length === 0) return null;

  return (
    <section className="py-12 bg-primary/5 border-y border-primary/10">
      <div className="container-max">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="flex-1 w-full space-y-6">
            <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
              <PlayCircle className="w-6 h-6 text-accent" />
              Active Learning Nodes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {enrolledCourses.map(course => (
                <div key={course.id} className="bg-white p-6 rounded-2xl shadow-sm border border-border transition-all hover:shadow-md">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-primary truncate pr-4 uppercase tracking-tighter text-sm">{course.title}</h3>
                    <span className="text-sm font-bold text-accent whitespace-nowrap">{course.progress}%</span>
                  </div>
                  <Progress value={course.progress} className="h-2 bg-primary/10" />
                  <div className="mt-4 flex justify-end">
                    <Link to={`/my-courses/${course.id}`}>
                      <Button variant="ghost" size="sm" className="text-primary font-black uppercase tracking-widest text-[10px] hover:text-accent gap-2">
                        Continue Transmission
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden lg:block w-px h-32 bg-border mx-4" />
          <div className="flex flex-col items-center gap-4 text-center min-w-[200px]">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-accent" />
            </div>
            <Link to="/my-courses">
              <Button className="btn-navy whitespace-nowrap text-[10px] font-black uppercase tracking-widest h-14 rounded-xl px-10">Vault Access</Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  return (
    <Layout>
      <HeroSection />
      <CourseProgress />
      <WhyChooseUs />
      <PricingSection />
      <BlogPreview />
      <CTASection />
    </Layout>
  );
};

export default Index;
